import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Typography } from "@mui/material";
import { AnimatePresence, animate, motion, useInView, useReducedMotion } from "framer-motion";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

import { CATEGORY_LABELS, formatRupees, getPlanAccent, getPlanIcon } from "./planTheme";

const VISIBLE_FEATURES = 5;

const useCountUp = (target, start) => {
  const reduceMotion = useReducedMotion();
  const [value, setValue] = useState(reduceMotion ? target : 0);

  useEffect(() => {
    if (reduceMotion) {
      setValue(target);
      return undefined;
    }
    if (!start) return undefined;

    const controls = animate(0, target, {
      duration: 1.1,
      ease: "easeOut",
      onUpdate: (latest) => setValue(latest),
    });
    return () => controls.stop();
  }, [target, start, reduceMotion]);

  return value;
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  show: (index) => ({
    opacity: 1,
    y: 0,
    transition: { delay: index * 0.08, duration: 0.5, ease: [0.22, 1, 0.36, 1] },
  }),
  exit: { opacity: 0, scale: 0.92, transition: { duration: 0.2 } },
};

// Hover lives on the inner card so leaving it doesn't replay the entrance delay.
const liftVariants = {
  rest: { y: 0 },
  hover: { y: -10 },
};

const iconVariants = {
  hover: { rotate: [0, -12, 10, 0], scale: 1.12, transition: { duration: 0.5 } },
};

const PlanPrice = ({ plan, accent, start }) => {
  const amount = useCountUp(Number(plan.price) || 0, start);

  if (plan.price_type === "free") {
    return (
      <Typography sx={{ fontSize: 40, fontWeight: 800, lineHeight: 1.1, color: accent.main }}>
        FREE
      </Typography>
    );
  }

  if (plan.price_type === "custom") {
    return (
      <Box>
        <Typography
          sx={{
            fontSize: 30,
            fontWeight: 800,
            lineHeight: 1.15,
            background: accent.gradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Customized
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.5 }}>
          {plan.price_suffix || "Tailored to your goals"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
      <Typography
        sx={{
          fontSize: 40,
          fontWeight: 800,
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
          background: accent.gradient,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        {formatRupees(amount)}
      </Typography>
      {plan.price_suffix && (
        <Typography sx={{ fontSize: 14, color: "#6B7280", fontWeight: 500 }}>
          {plan.price_suffix}
        </Typography>
      )}
    </Box>
  );
};

PlanPrice.propTypes = {
  plan: PropTypes.object.isRequired,
  accent: PropTypes.object.isRequired,
  start: PropTypes.bool.isRequired,
};

const FeatureItem = ({ text, accent, delay }) => (
  <Box
    component={motion.li}
    initial={{ opacity: 0, x: -12 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, height: 0 }}
    transition={{ delay, duration: 0.3 }}
    sx={{ display: "flex", gap: 1.2, alignItems: "flex-start", listStyle: "none" }}
  >
    <CheckCircleRoundedIcon sx={{ fontSize: 20, color: accent.main, mt: "1px", flexShrink: 0 }} />
    <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 1.5 }}>{text}</Typography>
  </Box>
);

FeatureItem.propTypes = {
  text: PropTypes.string.isRequired,
  accent: PropTypes.object.isRequired,
  delay: PropTypes.number.isRequired,
};

const PlanCard = ({ plan, index, onAction, preview = false }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [expanded, setExpanded] = useState(false);
  const accent = getPlanAccent(index);
  const Icon = getPlanIcon(plan);
  const features = plan.features || [];
  const visibleFeatures = expanded ? features : features.slice(0, VISIBLE_FEATURES);
  const hiddenCount = features.length - VISIBLE_FEATURES;
  const featured = Boolean(plan.is_featured);

  return (
    <Box
      ref={ref}
      component={motion.div}
      layout={!preview}
      variants={cardVariants}
      custom={preview ? 0 : index}
      initial="hidden"
      animate="show"
      exit="exit"
      sx={{ height: "100%", position: "relative", pt: featured ? 0 : { lg: 2 } }}
    >
      <Box
        component={motion.div}
        variants={liftVariants}
        initial="rest"
        animate="rest"
        whileHover="hover"
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        sx={{
          position: "relative",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: "24px",
          p: { xs: 2.5, sm: 3 },
          overflow: "hidden",
          border: featured ? "2px solid transparent" : "1px solid #EEF0F4",
          background: featured
            ? `linear-gradient(#fff, #fff) padding-box, ${accent.gradient} border-box`
            : "#fff",
          boxShadow: featured
            ? "0 24px 60px -20px rgba(234, 88, 12, 0.45)"
            : "0 10px 30px -18px rgba(41, 46, 92, 0.35)",
          transition: "box-shadow 0.3s ease",
          "&:hover": {
            boxShadow: `0 28px 60px -22px ${accent.main}88`,
          },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -70,
            right: -70,
            width: 180,
            height: 180,
            borderRadius: "50%",
            bgcolor: accent.soft,
          }}
        />

        {plan.badge && (
          <Box
            component={motion.span}
            initial={{ scale: 0, rotate: -12 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.3 + index * 0.08, type: "spring", stiffness: 260 }}
            sx={{
              position: "absolute",
              top: 18,
              right: 18,
              px: 1.5,
              py: 0.5,
              borderRadius: "999px",
              background: accent.gradient,
              color: "#fff",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: "uppercase",
              boxShadow: `0 6px 16px -6px ${accent.main}`,
              zIndex: 1,
            }}
          >
            {plan.badge}
          </Box>
        )}

        <Box
          component={motion.div}
          variants={iconVariants}
          sx={{
            position: "relative",
            width: 56,
            height: 56,
            borderRadius: "16px",
            background: accent.gradient,
            display: "grid",
            placeItems: "center",
            color: "#fff",
            boxShadow: `0 10px 22px -10px ${accent.main}`,
            mb: 2,
          }}
        >
          <Icon sx={{ fontSize: 30 }} />
        </Box>

        <Typography
          sx={{
            position: "relative",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: accent.main,
          }}
        >
          {CATEGORY_LABELS[plan.category] || plan.category}
        </Typography>
        <Typography
          component="h3"
          sx={{ position: "relative", fontSize: 21, fontWeight: 700, color: "#171725", mt: 0.5, pr: plan.badge ? 9 : 0 }}
        >
          {plan.title || "Untitled plan"}
        </Typography>
        {plan.tagline && (
          <Typography sx={{ position: "relative", fontSize: 14, color: "#6B7280", mt: 0.75, lineHeight: 1.5 }}>
            {plan.tagline}
          </Typography>
        )}

        <Box sx={{ my: 2.5 }}>
          <PlanPrice plan={plan} accent={accent} start={inView || preview} />
        </Box>

        <Box sx={{ height: "1px", bgcolor: "#EEF0F4", mb: 2 }} />

        <Box component="ul" sx={{ m: 0, p: 0, display: "grid", gap: 1.3 }}>
          <AnimatePresence initial={false}>
            {visibleFeatures.map((feature, featureIndex) => (
              <FeatureItem
                key={`${featureIndex}-${feature}`}
                text={feature}
                accent={accent}
                delay={featureIndex < VISIBLE_FEATURES ? 0.25 + featureIndex * 0.06 : (featureIndex - VISIBLE_FEATURES) * 0.04}
              />
            ))}
          </AnimatePresence>
        </Box>

        {hiddenCount > 0 && (
          <Button
            onClick={() => setExpanded((current) => !current)}
            endIcon={
              <ExpandMoreRoundedIcon
                sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.3s" }}
              />
            }
            sx={{
              alignSelf: "flex-start",
              mt: 1.5,
              px: 1,
              textTransform: "none",
              fontWeight: 600,
              color: accent.main,
            }}
          >
            {expanded ? "Show less" : `Show all ${features.length} services`}
          </Button>
        )}

        <Box sx={{ flexGrow: 1, minHeight: 20 }} />

        <Button
          fullWidth
          onClick={() => onAction?.(plan)}
          endIcon={<ArrowForwardRoundedIcon />}
          sx={{
            position: "relative",
            overflow: "hidden",
            mt: 1,
            height: 50,
            borderRadius: "14px",
            textTransform: "none",
            fontSize: 16,
            fontWeight: 700,
            color: "#fff",
            background: accent.gradient,
            boxShadow: `0 12px 24px -12px ${accent.main}`,
            "& .MuiButton-endIcon": { transition: "transform 0.25s" },
            "&:hover": { background: accent.gradient, filter: "brightness(1.05)" },
            "&:hover .MuiButton-endIcon": { transform: "translateX(4px)" },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: "-75%",
              width: "50%",
              height: "100%",
              background: "linear-gradient(120deg, transparent, rgba(255,255,255,0.45), transparent)",
              transform: "skewX(-20deg)",
              transition: "left 0.6s ease",
            },
            "&:hover::after": { left: "130%" },
          }}
        >
          {plan.cta_label || "Get Started"}
        </Button>
      </Box>
    </Box>
  );
};

PlanCard.propTypes = {
  plan: PropTypes.shape({
    plan_id: PropTypes.number,
    title: PropTypes.string,
    category: PropTypes.string,
    price_type: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    price_suffix: PropTypes.string,
    tagline: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.string),
    badge: PropTypes.string,
    is_featured: PropTypes.bool,
    cta_label: PropTypes.string,
    cta_link: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onAction: PropTypes.func,
  preview: PropTypes.bool,
};

export default PlanCard;
